import React, { FC, useRef } from 'react'
import moment from 'moment'
import { DatePicker as DatePickerAnt, Form } from 'antd'
import { GetFieldDecoratorOptions, WrappedFormUtils } from 'antd/lib/form/Form'
import locale from 'antd/lib/date-picker/locale/ru_RU'
import {
	DATE_AND_TIME_FORMAT,
	DATE_FORMAT,
	DATE_FORMAT_FOR_BACKEND,
	TIME_FORMAT_FULL,
} from 'Src/constants/Global'
import { DatePickerType, i18n, TooltipLabelComponent, UITypes } from 'LibNeo'
import { DatePickerProps, PickerProps, RangePickerProps } from 'antd/lib/date-picker/interface'
import { TimePickerProps } from 'antd/lib/time-picker'
import { createPortal } from 'react-dom'
import { CalendarWrapper, DatePickerWrapper, Icon } from '../styled'

const { MonthPicker: MonthPickerAnt } = DatePickerAnt
locale.lang.ok = 'Выбрать'

declare type DatePickerComponentProps = {
	item?: string
	initialValue?: any
	label?: string
	field?: string
	form?: WrappedFormUtils
	type?: DatePickerType
	disabled?: boolean
	disabledDate?: (current: moment.Moment | null) => boolean
	showToday?: boolean
	onChange?: any
	withoutTimezone?: boolean
	fieldOptions?: GetFieldDecoratorOptions
	tooltip?: string
	showTime?: TimePickerProps | boolean // добавляет возможность выбора времени наряду с датой
	defaultTime?: string // дефолтное время в формате TIME_FORMAT_FULL; будет использовано, если передан параметр showTime
	requiredHighlight?: boolean
	placeholder?: string
} & ((PickerProps & DatePickerProps) | RangePickerProps | { requiredHighlight?: boolean })

export const DatePickerComponent: FC<DatePickerComponentProps> = ({
	initialValue,
	field,
	label,
	placeholder,
	form,
	type = DatePickerType.DEFAULT,
	disabled = false, // гарантирует появление атрибута data-qa-disabled, ввиду типа boolean | undef
	disabledDate,
	fieldOptions,
	tooltip,
	showTime,
	showToday,
	defaultTime,
	onChange,
	withoutTimezone = true,
	requiredHighlight = false,
	...props
}) => {
	const portalRef = useRef(null)

	const onChangeValue = (value, options): void => {
		if (onChange) onChange(value, options)
	}

	const isRequired = React.useMemo(
		(): boolean => !!fieldOptions?.rules?.find((v): boolean => !!v.required),
		[]
	)

	const showTimeParams =
		showTime && defaultTime ? { defaultValue: moment(defaultTime, TIME_FORMAT_FULL) } : showTime

	const getDatePicker = (): JSX.Element => {
		const selectType = (): any => {
			switch (type) {
				case DatePickerType.MONTH:
					return MonthPickerAnt
				default:
					return DatePickerAnt
			}
		}

		const Picker = selectType()

		return (
			<Picker
				format={showTime ? DATE_AND_TIME_FORMAT : DATE_FORMAT}
				locale={locale}
				placeholder={
					Array.isArray(placeholder) ? placeholder.map((i): string => i18n(i)) : i18n(placeholder)
				}
				allowClear
				showToday={showToday}
				disabled={disabled}
				disabledDate={disabledDate}
				data-qa-disabled={disabled}
				showTime={showTimeParams}
				suffixIcon={<Icon name="calendar" type={UITypes.PASSIVE} />}
				onChange={onChangeValue}
				getCalendarContainer={() => {
					return portalRef.current
				}}
				{...props}
			/>
		)
	}

	return (
		<>
			<DatePickerWrapper requiredHighlight={requiredHighlight}>
				{(label || tooltip) && (
					<TooltipLabelComponent title={label} required={isRequired} tooltip={tooltip} />
				)}
				{form ? (
					<Form.Item>
						{form.getFieldDecorator(field, {
							...fieldOptions,
							getValueFromEvent: (m) => {
								const newDate = m
								if (withoutTimezone && newDate) {
									newDate.toJSON = () =>
										m.format(showTime ? DATE_AND_TIME_FORMAT : DATE_FORMAT_FOR_BACKEND)
								}
								return newDate
							},
							initialValue,
						})(getDatePicker())}
					</Form.Item>
				) : (
					getDatePicker()
				)}
				{createPortal(<CalendarWrapper ref={portalRef} />, document.body)}
			</DatePickerWrapper>
		</>
	)
}
